import { z } from 'zod';
import { IsoDatetimeToDateSchema } from './date-codecs.js';
import { VerifiedTypeSchema } from './followers.js';

export const TweetEmbeddableStatusSchema = z.enum(['allow', 'deny', 'unknown']);
export type TweetEmbeddableStatus = z.infer<typeof TweetEmbeddableStatusSchema>;

// Used when hydrating media variants across web + CLI exports.
export const TweetMediaVariantSchema = z.object({
  bitRate: z.number().optional(),
  contentType: z.string(),
  url: z.string(),
});
export type TweetMediaVariant = z.infer<typeof TweetMediaVariantSchema>;

// Shared media item shape consumed by tweet processor + canonical caches.
export const TweetMediaItemSchema = z.object({
  mediaKey: z.string(),
  type: z.enum(['photo', 'video', 'animated_gif']),
  url: z.string().optional(),
  previewImageUrl: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  altText: z.string().optional(),
  durationMs: z.number().optional(),
  variants: z.array(TweetMediaVariantSchema).optional(),
});
export type TweetMediaItem = z.infer<typeof TweetMediaItemSchema>;

export const TweetMediaDedupeDecisionSchema = z
  .object({
    matchedUrl: z.string(),
    linkImageUrl: z.string().nullable().optional(),
    linkImageHash: z.string().nullable().optional(),
    mediaUrl: z.string().nullable().optional(),
    mediaHash: z.string().nullable().optional(),
    distance: z.number().nullable().optional(),
    threshold: z.number(),
    hidden: z.boolean().optional(),
  })
  .passthrough();
export type TweetMediaDedupeDecision = z.infer<typeof TweetMediaDedupeDecisionSchema>;

export const TweetMediaMetadataEntrySchema = z
  .object({
    key: z.string(),
    type: z.enum(['photo', 'video', 'animated_gif']).optional(),
    altText: z.string().nullable().optional(),
    durationMs: z.number().nullable().optional(),
    previewImageUrl: z.string().nullable().optional(),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    variantUrl: z.string().nullable().optional(),
    variantBitrate: z.number().nullable().optional(),
    dedupe: TweetMediaDedupeDecisionSchema.optional(),
  })
  .passthrough();
export type TweetMediaMetadataEntry = z.infer<typeof TweetMediaMetadataEntrySchema>;

export const TweetAuthorAffiliationSchema = z.object({
  organizationProfileId: z.string(),
  organizationName: z.string().optional(),
  organizationUsername: z.string().optional(),
  badgeUrl: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
});
export type TweetAuthorAffiliation = z.infer<typeof TweetAuthorAffiliationSchema>;

export const TweetAuthorPublicMetricsSchema = z
  .object({
    followersCount: z.number(),
    followingCount: z.number(),
    tweetCount: z.number(),
    listedCount: z.number(),
  })
  .partial();
export type TweetAuthorPublicMetrics = z.infer<typeof TweetAuthorPublicMetricsSchema>;

// Canonical author profile pulled into web timeline + CLI outputs.
export const TweetAuthorSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable().optional(),
  verifiedType: VerifiedTypeSchema.default('none'),
  verified: z.boolean().optional(),
  createdAt: IsoDatetimeToDateSchema,
  description: z.string().optional(),
  publicMetrics: TweetAuthorPublicMetricsSchema.optional(),
  affiliations: z.array(TweetAuthorAffiliationSchema).optional(),
  primaryAffiliation: TweetAuthorAffiliationSchema.optional(),
});
export type TweetAuthor = z.infer<typeof TweetAuthorSchema>;

// Entities used by inline tweet renderer + link collectors.
export const TweetEntitiesSchema = z
  .object({
    hashtags: z
      .array(
        z.object({
          tag: z.string(),
          start: z.number().optional(),
          end: z.number().optional(),
        })
      )
      .optional(),
    mentions: z
      .array(
        z.object({
          username: z.string(),
          id: z.string().optional(),
          start: z.number().optional(),
          end: z.number().optional(),
          name: z.string().nullable().optional(),
          profileImageUrl: z.string().nullable().optional(),
        })
      )
      .optional(),
    urls: z
      .array(
        z.object({
          url: z.string(),
          expandedUrl: z.string(),
          displayUrl: z.string(),
          mediaKey: z.string().optional(),
          title: z.string().optional(),
          description: z.string().nullable().optional(),
          unwoundUrl: z.string().optional(),
          start: z.number().optional(),
          end: z.number().optional(),
        })
      )
      .optional(),
    cashtags: z
      .array(
        z.object({
          tag: z.string(),
          start: z.number().optional(),
          end: z.number().optional(),
        })
      )
      .optional(),
    annotations: z
      .array(
        z.object({
          start: z.number(),
          end: z.number(),
          probability: z.number(),
          type: z.string(),
          normalizedText: z.string(),
        })
      )
      .optional(),
  })
  .passthrough();
export type TweetEntities = z.infer<typeof TweetEntitiesSchema>;

// Attachments consumed by media transformers + exporters.
export const TweetAttachmentsSchema = z
  .object({
    mediaKeys: z.array(z.string()).optional(),
    pollIds: z.array(z.string()).optional(),
    mediaItems: z.array(TweetMediaItemSchema).optional(),
  })
  .passthrough();
export type TweetAttachments = z.infer<typeof TweetAttachmentsSchema>;

export const ReferencedTweetSchema = z.object({
  type: z.enum(['repliedTo', 'quoted', 'retweeted']),
  id: z.string(),
});
export type ReferencedTweet = z.infer<typeof ReferencedTweetSchema>;

export const ContextAnnotationSchema = z.object({
  domain: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
  }),
  entity: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
  }),
});
export type ContextAnnotation = z.infer<typeof ContextAnnotationSchema>;

// Metrics drive timeline sorting, cache snapshots, and analytics.
export const TweetMetricsSchema = z.object({
  likeCount: z.number(),
  retweetCount: z.number(),
  replyCount: z.number(),
  quoteCount: z.number(),
  bookmarkCount: z.number().optional(),
  impressionCount: z.number().optional(),
  viewCount: z.number().optional(),
});
export type TweetMetrics = z.infer<typeof TweetMetricsSchema>;

export const TweetSuggestionTypeSchema = z.object({
  canonical: z.enum(['inNetwork', 'outOfNetwork', 'promoted', 'organic', 'unknown']),
  raw: z.string().optional(),
});
export type TweetSuggestionType = z.infer<typeof TweetSuggestionTypeSchema>;

export const TweetAlgorithmicMetadataSchema = z
  .object({
    suggestionType: TweetSuggestionTypeSchema.optional(),
    fetchedAt: z.string().optional(),
    source: z.string().optional(),
  })
  .passthrough();
export type TweetAlgorithmicMetadata = z.infer<typeof TweetAlgorithmicMetadataSchema>;

// Reply settings surfaced in moderation tooling + UI badges.
export const TweetReplySettingsSchema = z.enum([
  'everyone',
  'mentionedUsers',
  'followers',
  'subscribers',
  'verified',
  'other',
]);
export type TweetReplySettings = z.infer<typeof TweetReplySettingsSchema>;

// Link metadata shared across timeline link panels and exports.
export const TweetLinkMetadataSchema = z
  .object({
    title: z.string().optional(),
    description: z.string().optional(),
    image: z.string().optional(),
    imageHash: z
      .object({
        algo: z.string(),
        value: z.string(),
      })
      .nullable()
      .optional(),
    favicon: z.string().nullable().optional(),
    siteName: z.string().optional(),
    url: z.string(),
    video: z.string().optional(),
    embeddableStatus: TweetEmbeddableStatusSchema,
    metadata: z.unknown().nullable().optional(),
    error: z.unknown().optional(),
    articleMarkdown: z.string().nullable().optional(),
  })
  .passthrough();
export type TweetLinkMetadata = z.infer<typeof TweetLinkMetadataSchema>;

export type TweetMetadataRecord = Record<string, TweetLinkMetadata>;

// Canonical tweet consumed by web timeline, caches, CLI, and exports.
const CanonicalTweetBaseSchema = z.object({
  id: z.string(),
  text: z.string(),
  authorId: z.string(),
  createdAt: IsoDatetimeToDateSchema,
  updatedAt: IsoDatetimeToDateSchema.optional(),
  author: TweetAuthorSchema.optional(),
  conversationId: z.string().optional(),
  selfThreadTweetId: z.string().optional(),
  inReplyToUserId: z.string().optional(),
  lang: z.string().optional(),
  noteTweet: z
    .object({
      text: z.string(),
    })
    .optional(),
  metrics: TweetMetricsSchema.optional(),
  likeCount: z.number().optional(),
  retweetCount: z.number().optional(),
  replyCount: z.number().optional(),
  quoteCount: z.number().optional(),
  impressionCount: z.number().optional(),
  bookmarkCount: z.number().optional(),
  algorithmic: TweetAlgorithmicMetadataSchema.optional(),
  entities: TweetEntitiesSchema.optional(),
  attachments: TweetAttachmentsSchema.optional(),
  media: z.array(TweetMediaItemSchema).nullable().optional(),
  mediaItems: z.array(TweetMediaItemSchema).optional(),
  mediaMetadata: z.array(TweetMediaMetadataEntrySchema).optional(),
  referencedTweets: z.array(ReferencedTweetSchema).optional(),
  authorAffiliations: z.array(TweetAuthorAffiliationSchema).optional(),
  authorPrimaryAffiliation: TweetAuthorAffiliationSchema.optional(),
  metadata: z.record(z.string(), TweetLinkMetadataSchema).optional(),
  isRetweet: z.boolean().optional(),
  isQuote: z.boolean().optional(),
  isPinned: z.boolean().optional(),
  isLiked: z.boolean().optional(),
  isRetweeted: z.boolean().optional(),
  isLikedByUser: z.boolean().optional(),
  isBookmarked: z.boolean().optional(),
  relevanceScore: z.number().optional(),
  editHistoryTweetIds: z.array(z.string()).optional(),
  contextAnnotations: z.array(ContextAnnotationSchema).optional(),
  possiblySensitive: z.boolean().optional(),
  replySettings: TweetReplySettingsSchema.optional(),
});

type CanonicalTweetRecursive = z.infer<typeof CanonicalTweetBaseSchema> & {
  quotedTweet?: CanonicalTweetRecursive | undefined;
  retweetedTweet?: CanonicalTweetRecursive | undefined;
};

export type CanonicalTweet = CanonicalTweetRecursive;
// We intentionally expose the `Canonical*` prefix here so contract consumers can distinguish shared DTOs
// from any app-specific aliases (most callers re-export this as `Tweet` locally when they need a shorter name).

export const CanonicalTweetSchema: z.ZodType<CanonicalTweet> = z.lazy(() =>
  CanonicalTweetBaseSchema.extend({
    quotedTweet: CanonicalTweetSchema.optional(),
    retweetedTweet: CanonicalTweetSchema.optional(),
  })
);

export const NormalizedTweetSchema = CanonicalTweetSchema;
export type NormalizedTweet = CanonicalTweet;

export const CanonicalTweetArraySchema = z.array(CanonicalTweetSchema);
export type CanonicalTweetArray = z.infer<typeof CanonicalTweetArraySchema>;

export type TweetMediaArray = TweetMediaItem[];

export const CanonicalTweetMetadataRecordSchema = z.record(z.string(), TweetLinkMetadataSchema);
